import { Response } from 'request-dot-js'

import {
  AnonymousUser,
  ArticleFlat,
  ArticleMeta,
  AuthHeaders,
  AuthWithHeaders,
  BreadcrumbsContext,
  CardDismissal,
  WizardTourDismissal,
  CategoryDetail,
  Comment,
  EducationPopover,
  FetchRequestState,
  HandbookV2,
  HomepageDoc,
  MemberProfile,
  NavSortOrder,
  RootState,
  ShareUser,
  SidebarTabs,
  UserColorByArticle,
  Workspace,
} from 'types'
import { Favorite, FavoriteId } from 'api/clients/FavoriteClient'

import { ArticleReaction } from 'api/clients/ReactionClient'
import { RequestedArticle } from 'api/clients/RequestedArticleClient'
import { type AssignedProperty, type Assignee, type Property } from 'api/clients/PropertyClient'
import { Todo } from 'api/clients/TodoClient'
import { ADD_SNIPPET, DELETE_SNIPPET, SET_SNIPPETS, SHOW_SNIPPETS_RAIL } from './constants'
import { Snippet } from 'api/clients/SnippetClient'
import { CommentActions } from './actions/comments'
import { PropertyActions } from './actions/properties'
import { SidebarActions } from './actions/sidebar'
import { TodosActions } from './actions/todos'
import { NavigationActions } from './actions/navigation'
import { Actions as SuggestionsActions } from './actions/suggestions'
import { Actions as ReviewsFetchRequestActions } from './actions/reviewsFetchRequests'
import { Actions as ReviewsActions } from './actions/reviews'
import { Actions as ArticleInformalFeedbackRequestsFetchRequestActions } from './actions/articleInformalFeedbackRequestsFetchRequests'
import { Suggestion } from 'api/clients/SuggestionsClient'
import { CardDismissalActions } from './actions/cardDismissals'
import { WizardTourDismissalActions } from './actions/wizardTourDismissals'
import { InboxThreadActions } from './actions/inboxThreads'
import { Actions as ReadReceiptsActions } from './actions/readReceipts'
import { ReadReceiptRequestsAndTasks } from 'api/clients/ReadReceiptClient'
import { Actions as InformalFeedbackRequestsActions } from './actions/informalFeedbackRequests'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { HandbookActions } from './actions/handbooks'
import { UnregisteredUserActionType } from './actions/unregisteredUser'
import { TimelineEvent } from 'screens/Activity/Types'
import { WorkspaceActions } from './actions/workspace'
import { TrackChangesActions } from './actions/trackChanges'
import { FocusModeActions } from './actions/focusMode'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { HighlightPropertiesByNameProps } from './actions/properties'
import { WebsocketConsumer } from 'providers/WebsocketProvider'
import { ITheme } from 'stores/Theme.types'

export function articleMetaSet(articleMeta: ArticleMeta) {
  return {
    type: 'ARTICLE_META_SET' as 'ARTICLE_META_SET',
    payload: articleMeta,
  }
}

export function articleMetaUpsert(articleMeta: Partial<ArticleMeta>) {
  return {
    type: 'ARTICLE_META_UPSERT' as 'ARTICLE_META_UPSERT',
    payload: articleMeta,
  }
}

export function articlePasswordSet(keyValue: { [key: string]: string }) {
  return {
    type: 'ARTICLE_PASSWORD_SET' as 'ARTICLE_PASSWORD_SET',
    payload: keyValue,
  }
}

export function authSet({ auth }: { auth: AuthWithHeaders }) {
  return {
    type: 'AUTH_SET' as 'AUTH_SET',
    payload: { auth },
  }
}

export function authMerge({ auth }: { auth: {} }) {
  return {
    type: 'AUTH_MERGE' as 'AUTH_MERGE',
    payload: { auth },
  }
}

export function authUnset() {
  return {
    type: 'AUTH_UNSET' as 'AUTH_UNSET',
  }
}

export function headersSet({ headers }: { headers: AuthHeaders }) {
  return {
    type: 'HEADERS_SET' as 'HEADERS_SET',
    payload: { headers },
  }
}

export function headersUnset() {
  return {
    type: 'HEADERS_UNSET' as 'HEADERS_UNSET',
  }
}

export interface Getter {
  response: Response
  key: string
}
export function getter(payload: Getter) {
  return {
    type: 'GETTER' as 'GETTER',
    payload,
  }
}

export function forceFinderRefresh() {
  return {
    type: 'FORCE_FINDER_REFRESH',
    payload: true,
  }
}

export function clearFinderRefresh() {
  return {
    type: 'FORCE_FINDER_REFRESH',
    payload: false,
  }
}

export function followeesAdd(payload: FolloweeAdd[]) {
  return {
    type: 'FOLLOWEES_ADD' as 'FOLLOWEES_ADD',
    payload,
  }
}

export interface FolloweeAdd {
  id: number
  followingId: number | null
}
export function followeeAdd(payload: FolloweeAdd) {
  return {
    type: 'FOLLOWEE_ADD' as 'FOLLOWEE_ADD',
    payload,
  }
}

export interface FolloweeRemove {
  id: number
}
export function followeeRemove(payload: FolloweeRemove) {
  return {
    type: 'FOLLOWEE_REMOVE' as 'FOLLOWEE_REMOVE',
    payload,
  }
}

export function workspacesSet(payload: RootState['workspace']['workspaces']) {
  return {
    type: 'WORKSPACES_SET' as 'WORKSPACES_SET',
    payload,
  }
}

export function markWorkspaceAsSeen(payload: Workspace['key']) {
  return {
    type: 'MARK_WORKSPACE_AS_SEEN' as 'MARK_WORKSPACE_AS_SEEN',
    payload,
  }
}

export function setIsFirstView(payload: { key: Workspace['key']; isFirstView: boolean }) {
  return {
    type: 'SET_IS_FIRST_VIEW' as 'SET_IS_FIRST_VIEW',
    payload,
  }
}

export function updateWorkspaceGithubIntegration(payload: { workspaceId: number; githubIntegrationEnabled: boolean }) {
  return {
    type: 'UPDATE_GITHUB_INTEGRATION' as 'UPDATE_GITHUB_INTEGRATION',
    payload,
  }
}

export function currentWorkspaceIdSet(payload: number) {
  return {
    type: 'CURRENT_WORKSPACE_ID_SET' as 'CURRENT_WORKSPACE_ID_SET',
    payload,
  }
}

export function portalDataSet(payload: RootState['portal']['portalData']) {
  return {
    type: 'PORTAL_DATA_SET' as 'PORTAL_DATA_SET',
    payload,
  }
}

export function portalReset() {
  return {
    type: 'PORTAL_RESET' as 'PORTAL_RESET',
  }
}

export function portalEditModeSet(payload: RootState['portal']['isEditMode']) {
  return {
    type: 'PORTAL_EDIT_MODE_SET' as 'PORTAL_EDIT_MODE_SET',
    payload,
  }
}

export function portalLoaded() {
  return {
    type: 'PORTAL_LOADED' as 'PORTAL_LOADED',
  }
}

export function addReaction(payload: ArticleReaction) {
  return {
    type: 'ADD_REACTION' as 'ADD_REACTION',
    payload,
  }
}

export function reactionRemove(articleId: string) {
  return {
    type: 'REACTION_REMOVE' as 'REACTION_REMOVE',
    payload: articleId,
  }
}

export function addRequestedArticle(payload: RequestedArticle) {
  return {
    type: 'ADD_REQUESTED_ARTICLE' as 'ADD_REQUESTED_ARTICLE',
    payload,
  }
}

export function updateRequestedArticle(id: string, payload: Partial<RequestedArticle>) {
  return {
    type: 'UPDATE_REQUESTED_ARTICLE' as 'UPDATE_REQUESTED_ARTICLE',
    payload: { ...payload, id },
  }
}

export const setWebsocketConnection = (connection: WebsocketConsumer) => {
  return {
    type: 'SET_WEBSOCKET_CONNECTION' as 'SET_WEBSOCKET_CONNECTION',
    payload: { connection },
  }
}

export const setCategories = (categories: CategoryDetail[]) => {
  return {
    type: 'CATEGORIES_SET' as 'CATEGORIES_SET',
    payload: { categories },
  }
}

export const setCommentPromptText = (text: string) => {
  return {
    type: CommentActions.SET_COMMENT_PROMPT_TEXT,
    payload: { text },
  }
}

export const setActiveComment = (id: number | undefined, focus: boolean) => {
  return {
    type: CommentActions.COMMENTS_SET_ACTIVE,
    payload: { id, focus },
  }
}

export const setHighlightedComment = (commentId: number | undefined) => {
  return {
    type: CommentActions.COMMENT_SET_HIGHLIGHTED,
    payload: commentId,
  }
}

export const setIsDeletingOrResolvingComment = (isDeletingOrResolving: boolean) => {
  return {
    type: CommentActions.COMMENTS_IS_DELETING_OR_RESOLVING_SET,
    payload: isDeletingOrResolving,
  }
}

export function setOpenFolders(key: string, ids: string[]) {
  return {
    type: 'OPEN_FOLDER_IDS_SET' as 'OPEN_FOLDER_IDS_SET',
    payload: { key, ids },
  }
}

export function setCardDismissals(names: CardDismissal[]) {
  return {
    type: CardDismissalActions.SET_CARD_DISMISSALS,
    payload: { names },
  }
}

export function createCardDismissal(name: CardDismissal) {
  return {
    type: CardDismissalActions.CREATE_CARD_DISMISSAL,
    payload: { name },
  }
}

export function setWizardTourDismissals(names: WizardTourDismissal[]) {
  return {
    type: WizardTourDismissalActions.SET_WIZARD_TOUR_DISMISSALS,
    payload: { names },
  }
}

export function createWizardTourDismissal(name: WizardTourDismissal) {
  return {
    type: WizardTourDismissalActions.CREATE_WIZARD_TOUR_DISMISSAL,
    payload: { name },
  }
}

export function setSidebarLocked(locked: boolean) {
  return {
    type: SidebarActions.SET_SIDEBAR_LOCKED,
    payload: { locked },
  }
}

export function closeSidebar() {
  return {
    type: SidebarActions.SET_SIDEBAR_LOCKED,
    payload: { locked: false },
  }
}

export const setSidebarActiveTab = (activeTab: SidebarTabs) => {
  return {
    type: SidebarActions.SET_SIDEBAR_ACTIVE_TAB,
    payload: {
      activeTab,
    },
  }
}

export function setSidebarWidth(width: number) {
  return { type: SidebarActions.SET_SIDEBAR_WIDTH, payload: width }
}

export const setInitialSidebarState = (locked: boolean = false) => {
  return {
    type: SidebarActions.SET_INITIAL_SIDEBAR_STATE,
    payload: {
      locked,
    },
  }
}

export function breadcrumbsContextSet(breadcrumbsContext: BreadcrumbsContext) {
  return {
    type: 'BREADCRUMBS_CONTEXT_SET' as 'BREADCRUMBS_CONTEXT_SET',
    payload: breadcrumbsContext,
  }
}

export function favoritesSet(favorites: Favorite[]) {
  return {
    type: 'FAVORITES_SET' as 'FAVORITES_SET',
    payload: favorites,
  }
}

export function addFavorite(favorite: Favorite) {
  return {
    type: 'ADD_FAVORITE',
    payload: favorite,
  }
}

export function deleteFavorite(favoriteId: FavoriteId) {
  return {
    type: 'DELETE_FAVORITE',
    payload: favoriteId,
  }
}

export function setCommandLineVisible(visible: boolean) {
  visible && trackEventInHeap(HeapEventType.CommandLine, { action: 'command_line_opened', type: '' })
  return {
    type: 'COMMAND_LINE_VISIBLE_SET' as 'COMMAND_LINE_VISIBLE_SET',
    payload: visible,
  }
}

export function setCreateFromTemplateModalVisible(visible: boolean) {
  return {
    type: 'CREATE_FROM_TEMPLATE_MODAL_VISIBLE_SET' as 'CREATE_FROM_TEMPLATE_MODAL_VISIBLE_SET',
    payload: visible,
  }
}

export function setOnboardingLoaderVisible(visible: boolean) {
  return {
    type: 'ONBOARDING_LOADER_VISIBLE_SET' as 'ONBOARDING_LOADER_VISIBLE_SET',
    payload: visible,
  }
}

export function setPopoverClosed(type: EducationPopover) {
  return {
    type: 'CLOSE_POPOVER' as 'CLOSE_POPOVER',
    payload: type,
  }
}

export function closeAllPopovers() {
  return {
    type: 'CLOSE_ALL_POPOVERS' as 'CLOSE_ALL_POPOVERS',
  }
}

export function showNextPopover(swapInfo: { close: EducationPopover; open: EducationPopover }) {
  return {
    type: 'SWAP_POPOVER' as 'SWAP_POPOVEr',
    payload: swapInfo,
  }
}

export function setSearchBarActive(active: boolean) {
  return {
    type: 'SEARCH_BAR_ACTIVE_SET' as 'SEARCH_BAR_ACTIVE_SET',
    payload: active,
  }
}

export function setVersionModalVisible(visible: boolean) {
  return {
    type: 'VERSION_MODAL_VISIBLE_SET' as 'VERSION_MODAL_VISIBLE_SET',
    payload: visible,
  }
}

export function setMoveModalVisible(visible: boolean) {
  return {
    type: 'MOVE_MODAL_VISIBLE_SET' as 'MOVE_MODAL_VISIBLE_SET',
    payload: visible,
  }
}

export function setShareWithTeamModalVisible(visible: boolean) {
  return {
    type: 'SHARE_WITH_TEAM_MODAL_VISIBLE_SET' as 'SHARE_WITH_TEAM_MODAL_VISIBLE_SET',
    payload: visible,
  }
}

export function setGlobalPaywallBannerDismissed(visible: boolean) {
  return {
    type: 'GLOBAL_PAYWALL_BANNER_VISIBLE_SET' as 'GLOBAL_PAYWALL_BANNER_VISIBLE_SET',
    payload: visible,
  }
}

export function addSmartLinks(titles: {}) {
  return {
    type: 'ADD_SMART_LINK' as 'ADD_SMART_LINK',
    payload: titles,
  }
}

export function updateSmartLinkHelper(articles: []) {
  return {
    type: 'UPDATE_SMART_LINK_ARTICLE_HELPER' as 'UPDATE_SMART_LINK_ARTICLE_HELPER',
    payload: articles,
  }
}

export const hideLeftNavRail = (value: boolean) => {
  const payload = { hideLeftNavRail: value }
  return { type: NavigationActions.NAVIGATION_HIDE_LEFT_RAIL, payload }
}

export const setPreferredSort = (value: NavSortOrder) => {
  const payload = { preferredSort: value }
  return { type: NavigationActions.NAVIGATION_SET_PREFERRED_SORT, payload }
}

export const workspaceDropdownOpen = (payload: boolean) => {
  return { type: NavigationActions.NAVIGATION_WORKSPACE_DROPDOWN_OPEN, payload }
}

export const setFloatingSidebar = (payload: boolean) => {
  // Do not open floating sidebar if the window is not focused
  return { type: 'FLOATING_SIDEBAR_SET', payload: payload && document.hasFocus() }
}

export const upsertUserColor = (payload: UserColorByArticle) => {
  return { type: 'COLOR_UPSERT' as 'COLOR_UPSERT', payload }
}

export const setDocumentSettingsModalVisible = (visible: boolean) => {
  return {
    type: 'DOCUMENT_SETTINGS_MODAL_VISIBLE_SET' as 'DOCUMENT_SETTINGS_MODAL_VISIBLE_SET',
    payload: visible,
  }
}

export type Action =
  | { type: 'ACCESS_USERS_SET'; payload: { string: ShareUser[] } }
  | { type: 'ADD_REACTION'; payload: ArticleReaction }
  | { type: 'ADD_REQUESTED_ARTICLE'; payload: RequestedArticle }
  | { type: 'FAVORITES_SET'; payload: Favorite[] }
  | { type: 'ADD_FAVORITE'; payload: Favorite }
  | { type: 'DELETE_FAVORITE'; payload: FavoriteId }
  | { type: 'ARTICLE_META_SET'; payload: ArticleFlat }
  | { type: 'BRANCH_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'BREADCRUMBS_CONTEXT_SET'; payload: BreadcrumbsContext }
  | { type: 'CHANNEL_INVITE_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'COMMAND_LINE_VISIBLE_SET'; payload: boolean }
  | { type: 'COLOR_UPSERT'; payload: UserColorByArticle }
  | { type: 'CATEGORIES_SET'; payload: { categories: CategoryDetail[] } }
  | { type: 'CREATE_FROM_TEMPLATE_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'ONBOARDING_LOADER_VISIBLE_SET'; payload: boolean }
  | { type: CommentActions.COMMENTS_IS_DELETING_OR_RESOLVING_SET; payload: boolean }
  | { type: CommentActions.NEW_COMMENT_ACTIVE_SET; payload: boolean }
  | { type: CommentActions.COMMENTS_SET_ACTIVE; payload: { id: RootState['comments']['activeId']; focus: boolean } }
  | { type: CommentActions.COMMENT_SET_HIGHLIGHTED; payload: RootState['comments']['highlightedId'] }
  | { type: CommentActions.SET_COMMENT_PROMPT_TEXT; payload: { text: string } }
  | { type: CommentActions.UPSERT_COMMENT; payload: { comment: Comment } }
  | { type: CommentActions.UPSERT_COMMENTS; payload: { comments: Comment[] } }
  | { type: CommentActions.MARK_COMMENT_AS_READ; payload: { id: number } }
  | { type: CommentActions.PIN_COMMENT; payload: { id: number } }
  | { type: CommentActions.UNPIN_COMMENT; payload: { id: number } }
  | { type: CommentActions.DELETE_COMMENT; payload: { id: number } }
  | { type: CommentActions.RESOLVE_COMMENT; payload: { id: number } }
  | { type: CommentActions.UNRESOLVE_COMMENT; payload: { id: number } }
  | { type: CommentActions.UPSERT_COMMENTS_HIDDEN_LAYERS_IDS; payload: { ids: string[] } }
  | { type: PropertyActions.REMOVE_PROPERTY; payload: { propertyId: number } }
  | { type: PropertyActions.UPSERT_PROPERTY; payload: { property: Property } }
  | { type: PropertyActions.UPSERT_PROPERTIES; payload: { properties: Property[] } }
  | { type: PropertyActions.HIGHLIGHT_PROPERTIES; payload: HighlightPropertiesByNameProps }
  | { type: TodosActions.UPSERT_TODO; payload: { todo: Todo } }
  | { type: TodosActions.UPSERT_TODOS; payload: { todos: Todo[] } }
  | { type: TodosActions.DELETE_TODO; payload: { id: number } }
  | { type: TodosActions.SET_ACTIVE_TODO; payload: { id?: number } }
  | { type: TodosActions.SET_HIGHLIGHTED_TODO; payload: { id?: number } }
  | { type: InboxThreadActions.SET_IS_LOADING; payload: { isLoading: boolean } }
  | {
      type: InboxThreadActions.UPDATE_UNREAD_INBOX_THREADS_COUNT
      payload: {
        unreadReviewsCount: number
        unreadTodosCount: number
      }
    }
  | { type: 'FORCE_FINDER_REFRESH'; payload: boolean }
  | {
      type: 'SET_FILES_PROPERTIES_LOADING_STATE'
      payload: { assignees: Assignee[]; propertiesIds: number[]; loadingState: FetchRequestState }
    }
  | { type: 'UPSERT_FILES_PROPERTIES'; payload: { assignedProperties: AssignedProperty[] } }
  | { type: 'DELETE_FILES_PROPERTIES'; payload: { assignees: Assignee[]; propertyId: number } }
  | { type: 'DELETE_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'LINKED_COPY_MODAL_CLOSE' }
  | { type: 'CURRENT_WORKSPACE_ID_SET'; payload: RootState['workspace']['currentWorkspaceId'] }
  | { type: 'FOLLOWEE_ADD'; payload: FolloweeAdd }
  | { type: 'FOLLOWEES_ADD'; payload: FolloweeAdd[] }
  | { type: 'FOLLOWEE_REMOVE'; payload: FolloweeRemove }
  | { type: 'GETTER'; payload: Getter }
  | { type: 'GLOBAL_PAYWALL_BANNER_VISIBLE_SET'; payload: boolean }
  | { type: 'MOVE_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'OPEN_FOLDER_IDS_SET'; payload: { key: string; ids: string[] } }
  | { type: 'PORTAL_DATA_SET'; payload: RootState['portal']['portalData'] }
  | { type: 'PORTAL_EDIT_MODE_SET'; payload: RootState['portal']['isEditMode'] }
  | { type: 'PORTAL_LOADED' }
  | { type: 'PORTAL_RESET' }
  | { type: 'REACTION_REMOVE'; payload: number }
  | { type: 'SEARCH_BAR_ACTIVE_SET'; payload: boolean }
  | { type: 'SHARE_WITH_TEAM_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'UPDATE_REQUESTED_ARTICLE'; payload: Partial<RequestedArticle> & { id: number } }
  | { type: 'VERSION_MODAL_VISIBLE_SET'; payload: boolean }
  | { type: 'WORKSPACES_SET'; payload: RootState['workspace']['workspaces'] }
  | { type: 'MARK_WORKSPACE_AS_SEEN'; payload: Workspace['key'] }
  | { type: 'SET_IS_FIRST_VIEW'; payload: { key: Workspace['key']; isFirstView: boolean } }
  | { type: 'SET_WEBSOCKET_CONNECTION'; payload: RootState['websocket'] }
  | { type: SidebarActions.SET_SIDEBAR_LOCKED; payload: { locked: boolean } }
  | { type: SidebarActions.SET_SIDEBAR_ACTIVE_TAB; payload: { activeTab: string } }
  | { type: SidebarActions.SET_INITIAL_SIDEBAR_STATE; payload: { locked: boolean; activeTab: string } }
  | { type: SidebarActions.SET_SIDEBAR_WIDTH; payload: number }
  | {
      type: NavigationActions.NAVIGATION_WORKSPACE_DROPDOWN_OPEN
      payload: RootState['navigation']['openWorkspaceDropdown']
    }
  | { type: NavigationActions.NAVIGATION_HIDE_LEFT_RAIL; payload: { hideLeftNavRail: boolean } }
  | { type: NavigationActions.NAVIGATION_SET_PREFERRED_SORT; payload: { preferredSort: NavSortOrder } }
  | { type: 'ANONYMOUS_USER_SET'; payload: AnonymousUser }
  | { type: 'SET_CURRENT_ARTICLE_ID'; payload: RootState['currentArticleId'] }
  | { type: 'RENAME_ANONYMOUS_USER'; payload: { displayName: string } }
  | { type: 'OPEN_POPOVER'; payload: EducationPopover }
  | { type: 'CLOSE_POPOVER'; payload: EducationPopover }
  | { type: 'CLOSE_ALL_POPOVERS' }
  | { type: 'SWAP_POPOVER'; payload: { close: EducationPopover; open: EducationPopover } }
  | { type: 'MERGE_CONFIG_SET'; payload: { showAllDeletions?: boolean } }
  | { type: 'MERGE_BRANCH_SET'; payload: ArticleFlat }
  | { type: 'MERGE_BASE_BRANCH_SET'; payload: ArticleFlat }
  | { type: 'TIMELINE_SET'; payload: { timeline: RootState['timeline'] } }
  | { type: 'LOAD_MORE_ON_TIMELINE'; payload: { timeline: RootState['timeline'] } }
  | { type: 'UPDATE_TIMELINE_EVENT'; payload: { event: TimelineEvent } }
  | { type: 'TIMELINE_RESET' }
  | { type: typeof SHOW_SNIPPETS_RAIL; payload: { show: boolean } }
  | { type: typeof SET_SNIPPETS; payload: { snippets: Snippet[] } }
  | { type: typeof DELETE_SNIPPET; payload: { id: number } }
  | { type: typeof ADD_SNIPPET; payload: { snippet: Snippet } }
  | { type: 'SUGGEST_CHANGES_MODAL_SET'; payload: RootState['suggestChangesModal'] }
  | { type: 'ADD_SMART_LINK'; payload: {} }
  | { type: HandbookActions.SET_HANDBOOK; payload: HandbookV2 }
  | { type: HandbookActions.UPDATE_HANDBOOK; payload: Partial<HandbookV2> }
  | { type: 'UPDATE_SMART_LINK_ARTICLE_HELPER'; payload: { articles: any[] } }
  | { type: SuggestionsActions.UPSERT_SUGGESTIONS; payload: { suggestions: Suggestion[] } }
  | { type: SuggestionsActions.UPSERT_SUGGESTION; payload: { suggestion: Suggestion } }
  | { type: ReviewsActions.RESET_REVIEWS; payload: { reviews: InformalFeedbackRequest[] } }
  | { type: ReviewsActions.UPSERT_REVIEWS; payload: { reviews: InformalFeedbackRequest[] } }
  | { type: ReviewsActions.UPSERT_REVIEW; payload: { review: InformalFeedbackRequest } }
  | {
      type: ReadReceiptsActions.UPSERT_READ_RECEIPTS
      payload: { articleId: string; readReceipts: ReadReceiptRequestsAndTasks }
    }
  | {
      type: ReadReceiptsActions.UPSERT_READ_RECEIPT
      payload: { articleId: string; readReceipt: ReadReceiptRequestsAndTasks }
    }
  | {
      type: ReadReceiptsActions.REMOVE_READ_RECEIPT
      payload: { articleId: string; readReceiptId: number }
    }
  | {
      type: InformalFeedbackRequestsActions.UPSERT_INFORMAL_FEEDBACK_REQUESTS
      payload: { informalFeedbackRequests: InformalFeedbackRequest[] }
    }
  | {
      type: InformalFeedbackRequestsActions.UPSERT_INFORMAL_FEEDBACK_REQUEST
      payload: { informalFeedbackRequest: InformalFeedbackRequest }
    }
  | {
      type: ArticleInformalFeedbackRequestsFetchRequestActions.UPSERT_ARTICLE_INFORMAL_FEEDBACK_REQUESTS_FETCH_REQUEST
      payload: { articleKey: string; state: FetchRequestState }
    }
  | { type: 'USER_ARTICLE_LIKES_SET'; payload: RootState['userArticleLikes'] }
  | { type: 'USER_ARTICLE_LIKES_ADD'; payload: string }
  | { type: 'USER_ARTICLE_LIKES_REMOVE'; payload: string }
  | {
      type: ReviewsFetchRequestActions.UPSERT_REVIEWS_FETCH_REQUEST
      payload: { state: FetchRequestState }
    }
  | { type: 'SET_WORKSPACE_NEW_DOC'; payload: { workspaceKey: string; key: string } }
  | { type: 'UPDATE_GITHUB_INTEGRATION'; payload: { workspaceId: number; githubIntegrationEnabled: boolean } }
  | { type: WorkspaceActions.SET_WORKSPACE_HOMEPAGE_DOC; payload: { workspaceKey: string; doc: HomepageDoc | null } }
  | { type: WorkspaceActions.UPSERT_MEMBER_PROFILE; payload: { workspaceId: number; profile: MemberProfile } }
  | { type: WorkspaceActions.UPDATE_THEME; payload: { workspaceKey: string; theme: ITheme } }
  | {
      type: WorkspaceActions.UPDATE_COLOR_SCHEME
      payload: { workspaceKey: string; color_scheme: Workspace['color_scheme'] }
    }
  | {
      type: WorkspaceActions.UPDATE_APP_LOGO_URL
      payload: { workspaceKey: string; app_logo_url: Workspace['app_logo_url'] }
    }
  | {
      type: WorkspaceActions.SET_WORKSPACE_PAYING
      payload: { workspaceKey: string; paying: boolean; can_start_free_trial: boolean }
    }
  | {
      type: WorkspaceActions.SET_WORKSPACE_COMPANY_SIZE_ENUM
      payload: { workspaceKey: string; company_size_enum: number }
    }
  | UnregisteredUserActionType
  | { type: TrackChangesActions.SET_ENABLED; payload: { enabled: boolean } }
  | { type: TrackChangesActions.SET_SHOW_CHANGES; payload: { showChanges: boolean } }
  | { type: FocusModeActions.FOCUS_MODE_SET; payload: { showFocusMode: boolean; manual: boolean } }
  | { type: 'FLOATING_SIDEBAR_SET'; payload: boolean }
  | { type: FocusModeActions.FOCUS_MODE_ENABLED_SET; payload: { enableFocusMode: boolean } }
  | { type: 'DOCUMENT_SETTINGS_MODAL_VISIBLE_SET'; payload: boolean }

export interface CardDismissalAction {
  type: CardDismissalActions.SET_CARD_DISMISSALS | CardDismissalActions.CREATE_CARD_DISMISSAL
  payload: { names?: CardDismissal[]; name?: CardDismissal }
}

export interface WizardTourDismissalAction {
  type: WizardTourDismissalActions.SET_WIZARD_TOUR_DISMISSALS | WizardTourDismissalActions.CREATE_WIZARD_TOUR_DISMISSAL
  payload: { names?: WizardTourDismissal[]; name?: WizardTourDismissal }
}
